/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Store
import useStore from '@store/index';

// Types
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseResetPassword,
	TBodyResetPassword,
} from '../types';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const resetPassword = (
	body: TBodyResetPassword
): Promise<TApiResponseResetPassword> => {
	return apiClient.post('/users/reset_password', {
		...body,
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useResetPassword = () => {
	const { t } = useTranslation();

	const { setUser: setUser } = useStore();
	const navigate = useNavigate();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: resetPassword,
		onSuccess: (data) => {
			notifications.show({
				title: t('global.success'),
				message: t('auth.passwordSuccessfullyUpdated'),
				color: 'var(--mantine-color-igluGreen-9)',
			});
			queryClient.setQueryData(
				[queryKeys.auth.user],
				data
			);
			setUser(data);
			navigate('/');
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
